import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Footer } from "@/components/Footer";
import { CONTACT } from "@/lib/siteData";
import { BRAND } from "@/lib/brand";

const UPDATED = "22 June 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-lg md:text-lg font-bold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-slate-600 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <div className="flex items-center gap-2 text-cyan-600">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold">Your Privacy</span>
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold text-slate-900">Privacy Policy</h1>
          <p className="mt-3 text-slate-600">Last updated: {UPDATED}</p>

          <p className="mt-8 text-sm md:text-base text-slate-600 leading-relaxed">
            {BRAND.name}{BRAND.formerName ? `, formerly ${BRAND.formerName},` : ""} ("AWARE", "we", "us") is an
            Australian water testing laboratory. This policy explains what personal information we
            collect, how we use it, and the choices you have. We handle personal information in
            accordance with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth).
          </p>

          <Section title="1. Information we collect">
            <p>Depending on how you use AWARE, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><b>Account details</b> — your name, email address, phone number and password (stored as a secure hash — we never store plain-text passwords).</li>
              <li><b>Booking &amp; property details</b> — service addresses, property type and access notes needed to collect and test your water samples.</li>
              <li><b>Water test results</b> — laboratory results and reports linked to your account and tested property.</li>
              <li><b>Payment details</b> — payments are processed by Stripe; we do not store your full card number on our systems.</li>
              <li><b>Communications</b> — emails, SMS messages and enquiries you exchange with us.</li>
              <li><b>Technical data</b> — basic log information (such as IP address and browser type) used for security and to keep the service running reliably.</li>
            </ul>
          </Section>

          <Section title="2. Signing in with Google or Facebook">
            <p>
              If you choose "Continue with Google" or "Continue with Facebook", we receive only the
              minimum information needed to sign you in: your provider user ID, your verified email
              address and your name. We do not request or receive access to your contacts, posts,
              friends, photos, files or any other provider data.
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Social sign-in is an authentication method only — it never changes what your AWARE account can access.</li>
              <li>You can connect or disconnect these sign-in methods at any time from <b>Account → Sign-in methods</b> while signed in.</li>
              <li>If you ask Facebook to delete your data, we automatically unlink your Facebook identity from your AWARE account. Your AWARE account and water test records remain available through your other sign-in methods.</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To provide water testing services: bookings, sample collection, laboratory analysis and delivering your results and reports.</li>
              <li>To operate your account, authenticate sign-ins and keep your data secure.</li>
              <li>To send service communications (booking confirmations, technician updates, results notifications, retest reminders).</li>
              <li>To process payments and issue receipts or invoices.</li>
              <li>To respond to enquiries and provide support.</li>
              <li>To meet our legal and record-keeping obligations.</li>
            </ul>
            <p>We do not sell your personal information.</p>
          </Section>

          <Section title="4. Who we share information with">
            <p>
              We share information only with trusted service providers who help us deliver the service,
              and only what they need for their role:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Payment processing (Stripe)</li>
              <li>Email and SMS delivery providers</li>
              <li>Scheduling and field-service tools used by our technicians</li>
              <li>Postal mail providers, where we send you physical letters</li>
              <li>Professional advisers or authorities where required by law</li>
            </ul>
            <p>
              If you booked through one of our referral partners, that partner may see the status of
              the booking they referred. Partners cannot access your wider account or other properties.
            </p>
          </Section>

          <Section title="5. Cookies and sessions">
            <p>
              We use a secure, httpOnly authentication cookie to keep you signed in, and essential
              cookies required for the site to function. We do not use third-party advertising cookies.
            </p>
          </Section>

          <Section title="6. Storage and security">
            <p>
              Your data is stored securely with encryption in transit. Access to personal information
              is restricted to authorised staff, protected by role-based permissions, and significant
              account events are audit-logged. No system can be guaranteed 100% secure, but we take
              reasonable steps to protect your information from misuse, loss and unauthorised access.
            </p>
          </Section>

          <Section title="7. Retention">
            <p>
              We keep water test results and related records for as long as your account is active and
              as required for laboratory record-keeping and legal obligations. You may ask us to delete
              your account and personal information — some records may need to be retained where the
              law requires it.
            </p>
          </Section>

          <Section title="8. Your rights">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access the personal information we hold about you.</li>
              <li>Ask us to correct information that is inaccurate or out of date.</li>
              <li>Ask us to delete your personal information (subject to legal retention requirements).</li>
              <li>Disconnect Google or Facebook sign-in from your account at any time.</li>
              <li>Opt out of non-essential communications using the unsubscribe options provided.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us using the details below. If you are not
              satisfied with our response, you can complain to the Office of the Australian Information
              Commissioner (OAIC) at oaic.gov.au.
            </p>
          </Section>

          <Section title="9. Contact us">
            <p>
              {BRAND.name}
              <br />
              {CONTACT.address}
              <br />
              Phone: {CONTACT.phone}
              <br />
              Email: <a href={`mailto:${CONTACT.email}`} className="text-cyan-700 font-semibold hover:underline">{CONTACT.email}</a>
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We may update this policy from time to time. The latest version will always be available
              at this page, with the "last updated" date shown above.
            </p>
          </Section>

          <p className="mt-12 text-sm">
            <Link to="/" className="font-semibold text-cyan-700 hover:underline">← Back to home</Link>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
